import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ShieldCheck, Lock, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import ConsentBlock from "./ConsentBlock";

/**
 * Itinerary acceptance modal — Sasha-side "lock & pay deposit" flow.
 *
 * Workflow:
 *   1. Modal opens with the deposit amount + full trip total + clickwrap
 *      terms text fetched from the backend (versioned).
 *   2. Client ticks the consent checkbox; submit is gated until ticked.
 *   3. On submit we POST /api/acceptance/itinerary with the itinerary
 *      snapshot + acceptance metadata; backend opens a Stripe Checkout
 *      session and returns its URL.
 *   4. We redirect the browser to Stripe; their hosted page handles
 *      card entry. Success bounces back to /acceptance/success.
 *
 * The IP, user-agent, exact terms version, and itinerary JSON snapshot
 * are all captured server-side as the legal record of acceptance.
 */
export default function AcceptItineraryModal({
  open,
  onClose,
  budget,
  sashaReply,
  tripRef,
  itineraryId,
  userEmail,
}) {
  const [terms, setTerms] = useState(null);
  const [consents, setConsents] = useState({ terms: false, sms: false, calls: false, marketing: false });
  const [depositAuth, setDepositAuth] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const needsContact = !userEmail;
  const contactOk = !needsContact || (/^\S+@\S+\.\S+$/.test(guestEmail.trim()) && guestPhone.replace(/\D/g, "").length >= 7);

  // Reservation = 10% of the trip, at least $100, at most $2,000 (server recomputes).
  const fullTotal = Number(budget?.total) || 0;
  const resMin = terms?.reservation_min_usd ?? 100;
  const resMax = terms?.reservation_max_usd ?? 2000;
  const depositAmount = Math.min(resMax, Math.max(resMin, Math.round(fullTotal * 0.1)));

  useEffect(() => {
    if (!open) return;
    setConsents({ terms: false, sms: false, calls: false, marketing: false });
    setDepositAuth(false);
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/acceptance/text/current");
        if (!cancelled) setTerms(r.data);
      } catch {
        if (!cancelled) {
          setTerms({
            version: "v3-2026-11",
            text: "Couldn't load latest terms — please refresh.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!consents.terms || !depositAuth || !terms || !contactOk) return;
    if (!fullTotal) {
      toast.error("This itinerary has no priced total yet.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/acceptance/itinerary", {
        itinerary_id: itineraryId || null,
        itinerary_snapshot: {
          budget,
          sasha_reply: sashaReply || null,
          trip_ref: tripRef,
          user_email: userEmail,
          contact_email: needsContact ? guestEmail.trim().toLowerCase() : undefined,
          contact_phone: needsContact ? guestPhone.trim() : undefined,
          snapshot_at: new Date().toISOString(),
        },
        deposit_amount_usd: depositAmount,
        deposit_currency: "usd",
        full_trip_amount_usd: fullTotal,
        trip_ref: tripRef || null,
        accepted_text_version: terms.version,
        origin_url: window.location.origin,
        consents: {
          ...consents,
          deposit_authorized: depositAuth,
        },
      });
      // Persist acceptance id so /acceptance/success can poll status.
      localStorage.setItem("jante_pending_acceptance", r.data.acceptance_id);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not start your payment — please retry."
      );
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-[var(--emerald)]/70 backdrop-blur-sm z-[70] flex items-center justify-center px-4"
      data-testid="accept-itinerary-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="accept-itinerary-title"
    >
      <div className="bg-[var(--ivory)] max-w-xl w-full max-h-[92vh] overflow-y-auto relative border-t-4 border-[var(--gold)] shadow-2xl">
        <button
          className="absolute top-4 right-4 text-[var(--muted)] hover:text-[var(--emerald)] z-10"
          onClick={onClose}
          aria-label="Close"
          data-testid="accept-modal-close"
        >
          <X size={18} />
        </button>

        <form onSubmit={submit} className="p-8">
          <div className="overline flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} className="text-[var(--gold)]" /> Lock this trip
          </div>
          <h2
            id="accept-itinerary-title"
            className="font-display text-3xl text-[var(--emerald)] mt-2 leading-tight"
          >
            Place your reservation
          </h2>
          <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">
            Your reservation holds this itinerary and is credited toward your
            trip when you book. A Janté concierge will call you back to finalise
            every detail. Payment is processed securely by{" "}
            <span className="text-[var(--brand-red)]">{terms?.provider === "hubspot" ? "HubSpot Payments" : "Stripe"}</span>.
          </p>

          {/* Totals */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="lux-card-dark !p-5">
              <div className="overline text-[var(--gold)]">Full trip total</div>
              <div
                className="font-display text-3xl mt-1 tabular-nums"
                data-testid="accept-modal-full-total"
              >
                ${fullTotal.toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ivory)]/60 mt-2">
                Subject to change until paid
              </div>
            </div>
            <div className="lux-card !p-5 border-2 border-[var(--brand-red)]">
              <div className="overline text-[var(--brand-red)]">Reservation</div>
              <div
                className="font-display text-3xl mt-1 tabular-nums text-[var(--brand-red)]"
                data-testid="accept-modal-deposit"
              >
                ${depositAmount.toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-2)] mt-2">
                Credited toward your trip
              </div>
              {terms?.provider === "hubspot" && terms?.checkout_amount_usd > 0 && depositAmount > terms.checkout_amount_usd && (
                <div
                  className="text-[11px] text-[var(--ink-2)] mt-3 pt-3 border-t border-[var(--gold)]/30 leading-relaxed"
                  data-testid="accept-modal-checkout-split"
                >
                  <b className="text-[var(--emerald)]">${terms.checkout_amount_usd.toLocaleString()}</b> paid today at checkout ·{" "}
                  <b>${(depositAmount - terms.checkout_amount_usd).toLocaleString()}</b> balance invoiced by your concierge
                </div>
              )}
            </div>
          </div>

          {/* Terms */}
          <div className="mt-6 p-4 bg-[var(--ivory-2)] border-l-2 border-[var(--gold)] text-xs text-[var(--ink-2)] leading-relaxed">
            <div className="overline text-[var(--brand-red)] mb-2">
              <ShieldCheck size={11} strokeWidth={1.5} className="inline mr-1" />
              Terms · {terms?.version || "loading…"}
            </div>
            {terms?.text || "Loading terms…"}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.16em] text-[var(--brand-red)]">
              <a href="/legal#bookings" target="_blank" rel="noreferrer" className="hover:text-[var(--emerald)]" data-testid="modal-link-bookings">Booking policy</a>
              <span aria-hidden="true">·</span>
              <a href="/legal#privacy" target="_blank" rel="noreferrer" className="hover:text-[var(--emerald)]" data-testid="modal-link-privacy">Privacy</a>
              <span aria-hidden="true">·</span>
              <a href="/legal#sms" target="_blank" rel="noreferrer" className="hover:text-[var(--emerald)]" data-testid="modal-link-sms">SMS consent</a>
              <span aria-hidden="true">·</span>
              <a href="/legal#calls" target="_blank" rel="noreferrer" className="hover:text-[var(--emerald)]" data-testid="modal-link-calls">Recorded calls</a>
              <span aria-hidden="true">·</span>
              <a href="/legal#documents" target="_blank" rel="noreferrer" className="hover:text-[var(--emerald)]" data-testid="modal-link-documents">Document control</a>
            </div>
          </div>

          {needsContact && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="accept-modal-contact">
              <label className="text-[10px] uppercase tracking-[0.16em] text-[var(--ink-2)]">
                Email for your concierge *
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="mt-1 w-full border border-[var(--gold)]/50 bg-white px-3 py-2.5 text-sm normal-case tracking-normal text-[var(--ink)] focus:outline-none focus:border-[var(--emerald)]"
                  data-testid="accept-modal-email"
                />
              </label>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[var(--ink-2)]">
                Callback phone *
                <input
                  type="tel"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="mt-1 w-full border border-[var(--gold)]/50 bg-white px-3 py-2.5 text-sm normal-case tracking-normal text-[var(--ink)] focus:outline-none focus:border-[var(--emerald)]"
                  data-testid="accept-modal-phone"
                />
              </label>
            </div>
          )}

          <label
            className="mt-5 flex items-start gap-3 cursor-pointer text-sm text-[var(--ink)]"
            data-testid="accept-modal-deposit-auth-label"
          >
            <input
              type="checkbox"
              checked={depositAuth}
              onChange={(e) => setDepositAuth(e.target.checked)}
              className="mt-1 flex-shrink-0"
              required
              data-testid="accept-modal-deposit-auth"
            />
            <span className="leading-snug">
              <span className="text-[var(--brand-red)]">*</span> I accept this itinerary and authorize Janté to charge my
              payment method <b>${depositAmount.toLocaleString()}</b> as my reservation.
              I understand pricing is subject to change until paid in full.
            </span>
          </label>

          <div className="mt-4">
            <ConsentBlock
              value={consents}
              onChange={setConsents}
              showCallRecording
              testIdPrefix="accept-modal-consent"
            />
          </div>

          <div className="mt-7 flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-xs uppercase tracking-[0.18em] text-[var(--ink-2)] hover:text-[var(--emerald)] px-4 py-2.5"
              data-testid="accept-modal-cancel"
            >
              Not yet
            </button>
            <button
              type="submit"
              disabled={!consents.terms || !depositAuth || !terms || !contactOk || submitting}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="accept-modal-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Redirecting…
                </>
              ) : (
                <>
                  <Lock size={14} strokeWidth={1.5} /> Place reservation · $
                  {depositAmount.toLocaleString()}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
